import { type QueryFunctionContext, useQuery } from "@tanstack/react-query";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { RouteNames } from "enums";
import { type BasePerformanceRequestFilterParams } from "features/Library/Common/models";
import {
  isUserDeleted,
  formatUserName,
  type PerformanceFilter,
  convertInteractionDisplayName,
} from "features/Library/Common/utils/performanceUtils";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { peopleReportingService } from "features/Reporting/services/peopleReportingService";
import { type PeopleActivityDetails } from "features/Reporting/types/people";
import { type ColumnOption } from "interfaces/columnOptions";
import { useMemo } from "react";
import moment from "moment";
import { MoboLink } from "components/MoboLink/MoboLink";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";
import { type UserAssetTypes } from "features/People/Users/Performance/types";

const getDetails = async ({
  queryKey,
}: QueryFunctionContext<
  [_name: string, filters: BasePerformanceRequestFilterParams, selectedType: string, selectedCategory: string]
>) => {
  let data: PeopleActivityDetails[];
  if (queryKey[3] === "outreach") {
    ({ data } = await peopleReportingService.activity.getPeopleOutreachDetails({ ...queryKey[1], type: queryKey[2] }));
  } else {
    ({ data } = await peopleReportingService.activity.getPeopleInteractionDetails({
      ...queryKey[1],
      type: queryKey[2],
    }));
  }
  return data.map((d, i) => ({ ...d, id: i }));
};

export interface Props {
  dateFilter: PerformanceFilter;
  selectedType: UserAssetTypes;
  selectedCategory: string;
}

export const PeopleActivityDrilldown = ({ dateFilter, selectedType, selectedCategory }: Props) => {
  const formatDateTime = useReportingFormatWithTimeZone();
  const detailsQuery = useQuery({
    queryFn: getDetails,
    queryKey: ["people activity details", dateFilter, selectedType, selectedCategory],
    enabled: !!selectedCategory,
  });

  const columnOptions: ColumnOption<PeopleActivityDetails>[] = useMemo(
    () => [
      {
        name: "TITLE",
        width: 8,
        render(item) {
          if (selectedType === "Flow Outreach" || selectedType.match(/email/i)) {
            return (
              <Tooltip
                target={
                  <TextTruncate>
                    <MoboLink to={`/${RouteNames.contentCommunications}/${RouteNames.emails}/${item.Id}`}>
                      {item.Title}
                    </MoboLink>
                  </TextTruncate>
                }
                content={`${item.Title}`}
              />
            );
          } else if (selectedType.match(/embedded\s+videos?/i)) {
            // Embedded videos have a different base route than standard videos
            return (
              <Tooltip
                target={
                  <TextTruncate>
                    <MoboLink to={`/${RouteNames.contentEmbeddedVideos}/${item.Id}`}>{item.Title}</MoboLink>
                  </TextTruncate>
                }
                content={`${item.Title}`}
              />
            );
          } else if (selectedType.match(/flow/i)) {
            return (
              <Tooltip
                target={
                  <TextTruncate>
                    <MoboLink to={`/${RouteNames.contentFlows}/${item.Id}`}>{item.Title}</MoboLink>
                  </TextTruncate>
                }
                content={`${item.Title}`}
              />
            );
          } else if (selectedType.match(/assessment/i)) {
            return (
              <Tooltip
                target={
                  <TextTruncate>
                    <MoboLink to={`/${RouteNames.contentAssessments}/${item.Id}`}>{item.Title}</MoboLink>
                  </TextTruncate>
                }
                content={`${item.Title}`}
              />
            );
          } else if (selectedType.match(/video/i)) {
            return (
              <Tooltip
                target={
                  <TextTruncate>
                    <MoboLink to={`/${RouteNames.contentVideos}/${item.Id}`}>{item.Title}</MoboLink>
                  </TextTruncate>
                }
                content={`${item.Title}`}
              />
            );
          } else if (selectedType.match(/pdf/i)) {
            return (
              <Tooltip
                target={
                  <TextTruncate>
                    <MoboLink to={`/${RouteNames.contentPdfs}/${item.Id}`}>{item.Title}</MoboLink>
                  </TextTruncate>
                }
                content={`${item.Title}`}
              />
            );
          } else if (selectedType.match(/survey/i)) {
            return (
              <Tooltip
                target={
                  <TextTruncate>
                    <MoboLink to={`/${RouteNames.contentSurveys}/${item.Id}`}>{item.Title}</MoboLink>
                  </TextTruncate>
                }
                content={`${item.Title}`}
              />
            );
          } else if (selectedType.match(/event/i)) {
            return (
              <Tooltip
                target={
                  <TextTruncate>
                    <MoboLink to={`/${RouteNames.contentEvents}/${item.Id}`}>{item.Title}</MoboLink>
                  </TextTruncate>
                }
                content={`${item.Title}`}
              />
            );
          } else if (selectedType.match(/messages?/i)) {
            return (
              <Tooltip
                target={
                  <TextTruncate>
                    <MoboLink to={`/${RouteNames.contentCommunications}/${RouteNames.messages}/${item.Id}`}>
                      {item.Title}
                    </MoboLink>
                  </TextTruncate>
                }
                content={`${item.Title}`}
              />
            );
          } else if (selectedType.match(/links?/i)) {
            return (
              <Tooltip
                target={
                  <TextTruncate>
                    <MoboLink to={`/${RouteNames.contentLinks}/${item.Id}`}>{item.Title}</MoboLink>
                  </TextTruncate>
                }
                content={`${item.Title}`}
              />
            );
          }
          return <Tooltip target={<TextTruncate>{item.Title}</TextTruncate>} content={`${item.Title}`} />;
        },
      },
      {
        name: "USER",
        width: 8,
        render(item) {
          const shouldShowLink = !isUserDeleted(item) && item.UserId !== null;
          const formattedName = formatUserName(item.FirstName, item.LastName);
          return (
            <Tooltip
              target={
                <TextTruncate className={shouldShowLink ? "userLink" : ""}>
                  {shouldShowLink ? (
                    <MoboLink to={`/${RouteNames.peopleUsers}/${item.UserId}`}>{formattedName}</MoboLink>
                  ) : (
                    formattedName
                  )}
                </TextTruncate>
              }
              content={formattedName}
            />
          );
        },
      },
      {
        name: "Department",
        width: 5,
        render(item) {
          const department = item.Department ?? "-";
          return <Tooltip target={<TextTruncate>{department}</TextTruncate>} content={department} />;
        },
      },
      {
        name: "JOB TITLE",
        width: 5,
        render(item) {
          const title = item.JobTitle ?? "-";
          return <Tooltip target={<TextTruncate>{title}</TextTruncate>} content={title} />;
        },
      },
      {
        name: "DATE",
        width: 5,
        render(item) {
          const date = moment.utc(`${item.Date} ${item.Time}`);
          const formattedDateTime = formatDateTime(date);
          const datePart = formattedDateTime.split(" ")[0];
          return datePart;
        },
      },
      {
        name: "TIME",
        width: 5,
        render(item) {
          const date = moment.utc(`${item.Date} ${item.Time}`);
          const formattedDateTime = formatDateTime(date);
          const timePart = formattedDateTime.split(" ").slice(1).join(" ");
          return timePart;
        },
      },
    ],
    [selectedType, formatDateTime],
  );

  return (
    <div className="people-activity-drilldown">
      <RequestStatusRenderer state={detailsQuery.status}>
        {detailsQuery.data && (
          <GenericPerformanceList
            title={convertInteractionDisplayName(selectedType)}
            rows={detailsQuery.data}
            columns={columnOptions}
            filter={dateFilter}
          />
        )}
      </RequestStatusRenderer>
    </div>
  );
};
